import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import jspreadsheet from 'jspreadsheet-ce';
import _ from 'lodash';
import { SensorCableTagService } from '@service/sensor-cable-tag/sensor-cable-tag.service';
import { SensorCableTag, SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import {
    generateColums,
    getTableWidth,
    getTableHeight,
    CellError,
    getCellHasError,
    SheetErrorType,
    isDISlot,
    isAISlot,
    isOutputSlot,
    isDOSlot,
    isThermalK,
} from '@models/sensor-cable-tag-sheet';
import { prepareExcelData } from '@service/sensor-cable-tag/sensor-to-excel-utils';
import { SensorCableExcelStyleService } from '@service/sensor-cable-tag/sensor-cable-excel-style';
import { ExcelSheetKeyEvent } from '@components/channel/channel-sheet/channel-sheet.component';
import { getSensorData } from '@service/sensor-cable-tag/excel-to-sensor-utils';
import { normalPinNum, relayPinNum, Terminal, terminalType, TerminalType } from 'icreate/common/models/slot';
import { TranslateService } from '@ngx-translate/core';
import { SensorCableTagPrintAPIService } from '@service/sensor-cable-tag/sensor-cable-tag-print-api.service';
import { DutyEngineerService } from '@service/duty-engineer.service';
import { DutyEngineer } from 'icreate/common/models/duty-engineer';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationWindowTypes } from '@components/confirmation-window/confirmation-window.component';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { OutputSetting } from 'icreate/common/models/output-settings';
import { AlarmGroup, OutputConsoleSetting } from 'icreate/common/models/alarm-group';
import { OutputSettingsService } from '@service/output-settings.service';
import { AlarmGroupConsoleSettingsService } from '@service/alarm-group-console-settings.service';

@Component({
    selector: 'app-sensor-cable-tag-channels',
    templateUrl: './sensor-cable-tag-channels.component.html',
    styleUrl: './sensor-cable-tag-channels.component.scss',
    standalone: false,
})
export class SensorCableTagChannelsComponent implements OnChanges, AfterViewInit, OnDestroy {
    readonly TABLE_HEADER_HEIGHT = 72;
    readonly TOOLBAR_HEIGHT = 46;
    readonly ERROR_ICON_RIGHT = 12;

    readonly ERROR_OVER_4 = SheetErrorType.OVER_4;
    readonly ERROR_OVER_7 = SheetErrorType.OVER_7;
    readonly ERROR_OVER_10 = SheetErrorType.OVER_10;
    readonly ERROR_OVER_40 = SheetErrorType.OVER_40;
    readonly ERROR_INVALID = SheetErrorType.INVALID;
    readonly ERROR_INVALID_NOCORE = SheetErrorType.INVALID_NOCORE;

    @Input() selectedSlot!: SensorTagSlot;
    @Input() selectedOutput: string;
    @Input() channelInput: string;
    @Input() vesselId: string;
    @Input() userCanEdit: boolean = true;
    @Output() channelsUpdated = new EventEmitter<boolean>();

    @ViewChild('spreadsheet') spreadsheet: ElementRef;
    @ViewChild('spreadsheetContainer') spreadsheetContainer: ElementRef;
    @ViewChild('errorsContainer') errorsContainer: ElementRef;

    private worksheets: jspreadsheet.JspreadsheetInstance;
    private slotChannels: SensorCableTag[];
    private outputType: TerminalType;
    private outputStartPin: number;
    private sensorRows: any[][] = null;
    private initalSensorRows: any[][] = null;
    private columns = [];
    private prevSelectedChannelCoord: { x1: number; x2: number; y1: number; y2: number };
    private keyPressEventString: string;
    private cellHasErrors: { [cellKey: string]: CellError } = {};
    private selectedTerminal: Terminal;
    private dutyEngineers: DutyEngineer[] = [];
    private digitalOutputSettings: OutputSetting[] = [];
    private analogOutputSettings: OutputSetting[] = [];
    private consoleSettings: (OutputConsoleSetting & { AlarmGroup: AlarmGroup })[] = [];

    isLoading: boolean = false;
    tableWidth: string;
    textColorIsOpen: boolean = false;
    textColor: string;
    fillColorIsOpen: boolean = false;
    fillColor: string;

    shouldOpenHelp: boolean = false;
    helpIsOpen: boolean = false;

    errorIconRight: string = null;
    errorIconLeft: string = null;
    showErrors: boolean = false;

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (this.helpIsOpen) {
            this.helpIsOpen = false;
            this.shouldOpenHelp = false;
            return;
        }

        if (this.shouldOpenHelp) {
            this.helpIsOpen = true;
        }

        if (this.showErrors && !this.errorsContainer.nativeElement.contains(event.target)) {
            this.showErrors = false;
        }
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onWindowChange() {
        this.updateTableWidth();
        this.cdr.detectChanges();
    }

    constructor(
        private sensorCableTagService: SensorCableTagService,
        private excelStylesService: SensorCableExcelStyleService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        private sensorTagPrint: SensorCableTagPrintAPIService,
        private dutyEngineerService: DutyEngineerService,
        private outputSettingsService: OutputSettingsService,
        private alarmGroupConsoleSettingsService: AlarmGroupConsoleSettingsService,
        private dialog: MatDialog
    ) {}

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (this.selectedSlot && (changes.selectedSlot || changes.selectedOutput)) {
            try {
                this.isLoading = true;
                this.worksheets = null;
                this.selectedTerminal = undefined;
                if (this.spreadsheet) this.spreadsheet.nativeElement.innerHTML = '';
                if (this.selectedSlot.ioType == 'DO' && !this.selectedOutput) return;

                this.slotChannels = (await this.sensorCableTagService.getSlotChannels(this.selectedSlot))?.sort(
                    (a, b) => Number(a?.pinNum) - Number(b?.pinNum)
                );

                if (isDOSlot(this.selectedSlot.ioType)) {
                    this.filterDOChannels();
                    this.digitalOutputSettings = await this.outputSettingsService.getAllDOTerminals(
                        this.selectedSlot.slotId
                    );
                    this.consoleSettings = await this.alarmGroupConsoleSettingsService.getOutputBySlotId(
                        this.selectedSlot.slotId
                    );
                } else if (isDISlot(this.selectedSlot.ioType)) {
                    this.dutyEngineers = await this.dutyEngineerService.getBySlotId(this.selectedSlot.slotId);
                } else if (isOutputSlot(this.selectedSlot.ioType)) {
                    this.analogOutputSettings = await this.outputSettingsService.getAllAOTerminals(
                        this.selectedSlot.slotId
                    );
                }
                this.columns = generateColums(this.selectedSlot.ioType, this.outputType);
                this.sensorRows = prepareExcelData(
                    this.slotChannels,
                    this.selectedSlot.ioType,
                    this.outputType,
                    this.outputStartPin,
                    this.dutyEngineers,
                    this.digitalOutputSettings,
                    this.analogOutputSettings,
                    this.consoleSettings
                );
                this.initalSensorRows = _.cloneDeep(this.sensorRows);
                this.restartValues();
                this.initSheet();
                this.updateTableWidth();
                await this.setColumnsStyles();
                this.channelsUpdated.emit(this.hasUnsavedChanges());
                this.cdr.detectChanges();
            } catch (err) {
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        }

        if (changes.channelInput) {
            this.filterRows();
        }
    }

    ngAfterViewInit() {
        this.spreadsheet.nativeElement.addEventListener('keydown', this.arrowKeyPress);
        this.spreadsheet.nativeElement.addEventListener('scroll', this.fixedHeaderWhileScrolling);
    }

    ngOnDestroy(): void {
        this.spreadsheet.nativeElement.removeEventListener('keydown', this.arrowKeyPress);
        this.spreadsheet.nativeElement.removeEventListener('scroll', this.fixedHeaderWhileScrolling);
    }

    filterDOChannels() {
        if (!isDOSlot(this.selectedSlot.ioType) || !this.selectedOutput) return;
        this.outputType = this.selectedOutput.split(' ')[0] as TerminalType;
        const letter = this.selectedOutput.split(' ')[1];
        let typeIndex = letter ? letter.toUpperCase().charCodeAt(0) - 65 + 1 : 1;

        this.outputStartPin = 0;
        let skipped: boolean = false;
        let found = false;
        for (let i = 0; i < this.selectedSlot.terminal.length; i++) {
            if (found) continue;
            const type = this.selectedSlot.terminal[i].type;
            const channelsToSkip = this.selectedSlot.terminal[i].num;
            if (type == this.outputType && typeIndex >= 2 && !skipped) {
                if (typeIndex == 2) skipped = true;
                typeIndex -= 1;
                this.outputStartPin += channelsToSkip;
            } else if (type != this.outputType) {
                this.outputStartPin += channelsToSkip;
            } else {
                this.selectedTerminal = this.selectedSlot.terminal[i];
                found = true;
            }
        }

        const terminalNumChannels = this.outputType == terminalType.normal ? normalPinNum : relayPinNum;

        this.slotChannels = this.slotChannels.filter(
            sensor =>
                Number(sensor.pinNum) > this.outputStartPin &&
                Number(sensor.pinNum) <= this.outputStartPin + terminalNumChannels
        );
    }

    restartValues() {
        this.cellHasErrors = {};
        this.prevSelectedChannelCoord = null;
        this.keyPressEventString = null;
        this.textColorIsOpen = false;
        this.textColor = null;
        this.fillColorIsOpen = false;
        this.fillColor = null;
        this.shouldOpenHelp = false;
        this.helpIsOpen = false;
        this.errorIconRight = null;
        this.errorIconLeft = null;
        this.showErrors = false;
    }

    updateTableWidth(visibleRows?) {
        if (!this.spreadsheet) {
            this.tableWidth = null;
            return;
        }
        const tableWidth = getTableWidth(this.selectedSlot.ioType, this.outputType);
        const tableHeight = getTableHeight(this.selectedSlot.ioType, this.outputType, visibleRows);
        const tableStart = this.spreadsheetContainer.nativeElement.getBoundingClientRect().top;

        const scrollBarWidth = 20;
        const totalTableHeight = tableHeight + tableStart + this.TOOLBAR_HEIGHT;

        if (totalTableHeight > window.innerHeight) {
            this.tableWidth = tableWidth + scrollBarWidth + 'px';
        } else {
            this.tableWidth = tableWidth + 'px';
        }

        this.errorIconRight = null;
        this.errorIconLeft = null;

        if (tableWidth > window.innerWidth) {
            const excelContainer = document.querySelectorAll('.jexcel_container')[0];
            if (excelContainer.scrollHeight > excelContainer.clientHeight) {
                this.errorIconRight = this.ERROR_ICON_RIGHT + scrollBarWidth + 'px';
            } else {
                this.errorIconRight = this.ERROR_ICON_RIGHT + 'px';
            }
        } else {
            this.errorIconLeft = tableWidth - this.ERROR_ICON_RIGHT * 3 + 'px';
        }
    }

    initSheet() {
        const workSheets = jspreadsheet(this.spreadsheet.nativeElement, {
            tableOverflow: true,
            data: this.sensorRows,
            columns: this.columns,
            minDimensions: [this.columns.length, 0],
            loadingSpin: true,
            allowExport: false,
            allowDeleteRow: false,
            allowInsertRow: false,
            allowDeleteColumn: false,
            allowInsertColumn: false,
            allowManualInsertRow: false,
            allowManualInsertColumn: false,
            allowDeletingAllRows: false,
            updateTable: function (instance, cell, col, row, val, label, cellName) {}.bind(this),
            toolbar: [
                {
                    id: 'undo',
                    type: 'i',
                    content: 'undo',
                    onclick: () => {
                        this.handleUndo(workSheets);
                    },
                },
                {
                    id: 'redo',
                    type: 'i',
                    content: 'redo',
                    onclick: () => {
                        this.handleRedo(workSheets);
                    },
                },
                {
                    id: 'line1',
                    type: 'i',
                    content: 'custom element',
                },
                {
                    type: 'select',

                    k: 'font-family',
                    v: ['Arial', 'Verdana', 'Montserrat'],
                    selectedValue: this.excelStylesService.DEFAULT_FAMILY_FONT,
                },
                {
                    type: 'select',
                    k: 'font-size',
                    v: ['9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '17px', '18px', '19px', '20px'],
                    selectedValue: this.excelStylesService.DEFAULT_FONT_SIZE,
                },
                {
                    id: 'align_left',
                    type: 'i',
                    content: 'format_align_left',
                    k: 'text-align',
                    v: 'left',
                },
                {
                    id: 'align_center',
                    type: 'i',
                    content: 'format_align_center',
                    k: 'text-align',
                    v: 'center',
                },
                {
                    id: 'align_right',
                    type: 'i',
                    content: 'format_align_right',
                    k: 'text-align',
                    v: 'right',
                },
                {
                    id: 'format_bold',
                    type: 'i',
                    content: 'format_bold',
                    k: 'font-weight',
                    v: 'bold',
                },
                {
                    id: 'format_color_text',
                    type: 'i',
                    content: 'format_color_text',
                    k: 'color',
                    onclick: () => {
                        this.textColorIsOpen = !this.textColorIsOpen;
                        this.fillColorIsOpen = false;
                    },
                },
                {
                    id: 'format_color_fill',
                    type: 'i',
                    content: 'format_color_fill',
                    k: 'background-color',
                    onclick: () => {
                        this.fillColorIsOpen = !this.fillColorIsOpen;
                        this.textColorIsOpen = false;
                    },
                },
                {
                    id: 'help_outline',
                    type: 'i',
                    content: 'help_outline',
                    onclick: () => {
                        this.shouldOpenHelp = !this.shouldOpenHelp;
                    },
                },
            ],
            onselection: (instance, x1, y1, x2, y2, origin) => {
                this.prevSelectedChannelCoord = { x1, y1, x2, y2 };
                this.keyPressEventMapping(x1, y1, x2, y2);
                this.selectionActive(x1, y1, x2, y2);
            },
            onchangestyle: () => {
                this.updateHeaderStyles();
                const out = setTimeout(() => {
                    this.checkChannelSheetChange(workSheets);
                    clearTimeout(out);
                }, 0);
            },
            onchange: (element, cell, colIndex, rowIndex, newValue, oldValue) => {
                this.checkCellValidation(cell, Number(colIndex), Number(rowIndex), newValue);

                const out = setTimeout(() => {
                    this.checkChannelSheetChange(workSheets);
                    clearTimeout(out);
                }, 0);
            },
            onbeforechange: (element, cell, colIndex, rowIndex, newValue) => {
                if (cell.classList.contains('readonly')) {
                    return;
                }
                return newValue;
            },
            onbeforepaste: (elem, copiedText, colIndex, rowIndex) => {
                return copiedText;
            },
        });

        this.worksheets = workSheets;
        this.worksheets.ignoreHistory = true;
        this.worksheets.colgroup[0].style.visibility = 'collapse';
        this.worksheets.hideIndex();

        if (this.isThermalK()) {
            const commsString = this.translateService.instant('sensor-cable-tag.thermalK.com');
            this.worksheets.setValue('B17', commsString, true);
            this.worksheets.setMerge('B17', 4, 4);
        }

        this.excelStylesService.setDefaultStyle(this.worksheets, this.selectedSlot.ioType, this.outputType);

        this.worksheets.resetSelection();
        this.setheaderIconEvents();

        const helpIcon = document.getElementById('help_outline');
        if (this.isDORelaySlot() || this.isOutputSlot()) {
            helpIcon.style.opacity = '0.3';
            helpIcon.classList.add('disable');
        } else {
            helpIcon.style.opacity = '1';
        }

        this.worksheets.ignoreHistory = false;
        this.checkChannelSheetChange(workSheets);
    }

    filterRows() {
        if (!this.sensorRows || this.sensorRows.length == 0) return;
        if (!this.channelInput || this.channelInput.length == 0) {
            for (let i = 0; i < this.sensorRows.length; i++) {
                if (this.worksheets.rows[i].style.visibility == 'collapse') {
                    this.worksheets.rows[i].style.visibility = 'revert';
                }
            }
            this.updateTableWidth();
            return;
        }
        let itemNameIndex = 2;
        if (isOutputSlot(this.selectedSlot.ioType)) {
            itemNameIndex = 1;
        }

        const rowsToCollapse = this.sensorRows
            .map((row, index) => {
                const nameRow = this.isDORelaySlot() ? this.sensorRows[Math.floor(index / 3) * 3] : row;
                const itemName: string = nameRow[itemNameIndex];
                const hasItem = itemName && itemName.toLowerCase().includes(this.channelInput.toLowerCase());
                if (!hasItem) return index;
            })
            .filter(index => index || index == 0);

        const collpasedRowsSet = new Set(rowsToCollapse);

        for (let i = 0; i < this.sensorRows.length; i++) {
            if (collpasedRowsSet.has(i)) {
                this.worksheets.rows[i].style.visibility = 'collapse';
            } else {
                this.worksheets.rows[i].style.visibility = 'revert';
            }
        }

        const visibleRows = this.worksheets.rows.length - collpasedRowsSet.size;
        this.updateTableWidth(visibleRows);
    }

    setheaderIconEvents() {
        this.worksheets.ignoreHistory = true;
        document.getElementById('format_color_text').addEventListener('click', function (event) {
            event.stopPropagation();
            event.preventDefault();
        });

        document.getElementById('format_color_fill').addEventListener('click', function (event) {
            event.stopPropagation();
            event.preventDefault();
        });
        this.worksheets.ignoreHistory = false;
    }

    checkChannelSheetChange(worksheets: jspreadsheet.JspreadsheetInstance) {
        this.updateUndoButtonState(worksheets);
        this.updateRedoButtonState(worksheets);
        this.updateSaveButton();
    }

    handleUndo(worksheets: jspreadsheet.JspreadsheetInstance) {
        if (worksheets.historyIndex >= 0) {
            const action = worksheets.history[worksheets.historyIndex];
            const actionType = action?.action;
            worksheets.undo();
            if (actionType == 'setValue') {
                if (!action?.records || !action?.records[0]) return;
                const record = action?.records[0];
                const cell = worksheets.getCell([record.x, record.y]);
                if (!cell) return;
                this.checkCellValidation(cell, Number(record.col), Number(record.row), record.oldValue);
            }
            this.checkChannelSheetChange(worksheets);
        }
    }

    handleRedo(worksheets: jspreadsheet.JspreadsheetInstance) {
        if (worksheets.historyIndex < this.worksheets.history.length - 1) {
            const action = worksheets.history[worksheets.historyIndex + 1];
            const actionType = action?.action;
            worksheets.redo();
            if (actionType == 'setValue') {
                if (!action?.records || !action?.records[0]) return;
                const record = action?.records[0];
                const cell = worksheets.getCell([record.x, record.y]);
                if (!cell) return;
                this.checkCellValidation(cell, Number(record.col), Number(record.row), record.newValue);
            }
            this.checkChannelSheetChange(worksheets);
        }
    }

    updateUndoButtonState(worksheets: jspreadsheet.JspreadsheetInstance) {
        const undoBtn = document.querySelector('i#undo') as HTMLElement;
        if (!undoBtn) return;
        if (worksheets.historyIndex === -1) {
            undoBtn.style.color = '#cccccc';
            undoBtn.style.pointerEvents = 'none';
        } else {
            undoBtn.style.color = '#000000';
            undoBtn.style.pointerEvents = 'auto';
        }
    }

    updateRedoButtonState(worksheets: jspreadsheet.JspreadsheetInstance) {
        const redoBtn = document.querySelector('i#redo') as HTMLElement;
        if (!redoBtn) return;
        if (worksheets.historyIndex === this.worksheets.history.length - 1) {
            redoBtn.style.color = '#cccccc';
            redoBtn.style.pointerEvents = 'none';
        } else {
            redoBtn.style.color = '#000000';
            redoBtn.style.pointerEvents = 'auto';
        }
    }

    fixedHeaderWhileScrolling = event => {
        const header = event.target.querySelectorAll('thead')[0];
        const offset = event.target.scrollTop;
        if (!header) return;
        header.style.transform = `translateY(${offset}px)`;

        const dropDownCloseElement = document.getElementsByClassName('jdropdown-close');
        if (dropDownCloseElement && dropDownCloseElement?.length > 0) {
            ['mousedown', 'mouseup'].forEach(type => {
                (dropDownCloseElement[0] as HTMLElement).dispatchEvent(
                    new MouseEvent(type, { bubbles: true, cancelable: true, view: window })
                );
            });
        }
    };

    async setColumnsStyles() {
        this.worksheets.ignoreHistory = true;
        const currentStyles: any = this.worksheets.getStyle();
        const slotStyles = await this.excelStylesService.setSlotStyles(
            currentStyles,
            this.selectedSlot.slotId,
            this.selectedOutput
        );

        if (slotStyles) {
            this.worksheets.setStyle(slotStyles);
        }
        this.worksheets.ignoreHistory = false;
    }

    updateTextColor(newColor) {
        let selectedCellsCoords = this.worksheets.selectedCell;
        if (!selectedCellsCoords) {
            if (!this.prevSelectedChannelCoord) {
                return;
            }
            const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
            selectedCellsCoords = [x1, y1, x2, y2];
        }
        const selectedCells: any[] = this.excelStylesService.getSelectedCells(selectedCellsCoords);
        if (!selectedCells || selectedCells.length == 0) return;
        selectedCells.forEach(cell => {
            const cellData = this.worksheets.getCell(cell);
            if (!cellData.classList.contains('readonly')) {
                this.worksheets.setStyle(cell, 'color', newColor, true);
            }
        });
        const colorTextIcon = document.getElementById('format_color_text');
        colorTextIcon.style.color = newColor;
        this.textColor = newColor;
        this.textColorIsOpen = false;
        const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
    }

    updateCellColor(newColor) {
        let selectedCellsCoords = this.worksheets.selectedCell;
        if (!selectedCellsCoords) {
            if (!this.prevSelectedChannelCoord) {
                return;
            }
            const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
            selectedCellsCoords = [x1, y1, x2, y2];
        }
        const selectedCells: any[] = this.excelStylesService.getSelectedCells(selectedCellsCoords);
        if (!selectedCells || selectedCells.length == 0) return;
        selectedCells.forEach(cell => {
            const cellData = this.worksheets.getCell(cell);
            if (!cellData.classList.contains('readonly')) {
                this.worksheets.setStyle(cell, 'background-color', newColor, true);
            }
        });
        const colorFillIcon = document.getElementById('format_color_fill');
        colorFillIcon.style.color = newColor;
        this.fillColor = newColor;
        this.fillColorIsOpen = false;
        const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
    }

    updateSaveButton() {
        this.channelsUpdated.emit(this.hasUnsavedChanges());
    }

    keyPressEventMapping = (x1, y1, x2, y2) => {
        if (
            x1 === x2 &&
            y1 === y2 &&
            (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_RIGHT ||
                this.keyPressEventString === ExcelSheetKeyEvent.ARROW_LEFT)
        ) {
            let selected = this.worksheets.colgroup[x1];
            let count = 0;

            if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_RIGHT) {
                while (selected && selected.style.visibility === 'collapse') {
                    count += 1;
                    selected = this.worksheets.colgroup[x1 + count];
                }
            } else if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_LEFT) {
                while (selected && selected.style.visibility === 'collapse') {
                    count -= 1;
                    selected = this.worksheets.colgroup[x1 + count];
                }
            }

            if (!selected) {
                if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_RIGHT) {
                    this.worksheets.updateSelectionFromCoords(x1 - 1, y1, x2 - 1, y2);
                } else if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_LEFT) {
                    this.worksheets.updateSelectionFromCoords(x1 + 1, y1, x2 + 1, y2);
                }
                this.keyPressEventString = '';
                return;
            }

            if (!_.isEqual(this.worksheets.colgroup[x1], selected)) {
                this.worksheets.updateSelectionFromCoords(x1 + count, y1, x2 + count, y2);
                this.arrowKeyPress({
                    key: this.keyPressEventString,
                    target: document.querySelectorAll('.jexcel_container')[0] as EventTarget,
                } as KeyboardEvent);
            }
        }
    };

    async selectionActive(x1: number, y1: number, x2: number, y2: number) {
        this.updateHeaderStyles();
        if (!_.isEqual(this.prevSelectedChannelCoord, { x1, y1, x2, y2 })) {
            this.prevSelectedChannelCoord = { x1, y1, x2, y2 };
        }
    }

    private updateHeaderStyles() {
        const selectedCellsCoords = this.worksheets.selectedCell;
        if (!selectedCellsCoords) {
            return;
        }
        const selectedCells: any[] = this.excelStylesService.getSelectedCells(selectedCellsCoords);
        let selectedCellsStyles = {};
        selectedCells.forEach(cell => {
            const style = this.worksheets.getStyle(cell);
            selectedCellsStyles[cell] = style;
        });
        this.excelStylesService.updateToolbar(selectedCellsStyles, this.spreadsheet);
        const colorTextIcon = document.getElementById('format_color_text');
        this.textColor = colorTextIcon.style.color;
        const colorFillIcon = document.getElementById('format_color_fill');
        this.fillColor = colorFillIcon.style.color;
    }

    arrowKeyPress = (event: KeyboardEvent) => {
        if (event.key === ExcelSheetKeyEvent.ARROW_RIGHT) {
            const tr = this.worksheets.getSelectedRows()[0] as HTMLElement;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1] as HTMLElement;

            const { width: currentPosition } = (event.target as HTMLElement).getBoundingClientRect();
            const { width, right: outOfPosition } = td.nextElementSibling.getBoundingClientRect();

            if (outOfPosition >= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    left: width + (outOfPosition - currentPosition),
                });
            }
            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_RIGHT;
        } else if (event.key === ExcelSheetKeyEvent.ARROW_LEFT) {
            const tr = this.worksheets.getSelectedRows()[0] as HTMLElement;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1] as HTMLElement;

            const currentPosition = 0;
            const { width, left: outOfPosition } = td.previousElementSibling.getBoundingClientRect();

            if (outOfPosition <= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    left: -width + outOfPosition,
                });
            }

            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_LEFT;
        } else if (event.key === ExcelSheetKeyEvent.ARROW_DOWN) {
            const tr = (this.worksheets.getSelectedRows()[0] as HTMLElement).nextElementSibling;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1];

            const { height: currentPosition } = (event.target as HTMLElement).getBoundingClientRect();
            const { height, bottom: outOfPosition } = (td as HTMLElement).getBoundingClientRect();

            if (outOfPosition - this.TABLE_HEADER_HEIGHT >= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    top: height,
                });
            }

            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_DOWN;
        } else if (event.key === ExcelSheetKeyEvent.ARROW_UP) {
            const tr = (this.worksheets.getSelectedRows()[0] as HTMLElement).previousElementSibling;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1];

            const { top: currentPosition } = (event.target as HTMLElement).getBoundingClientRect();
            const { height, top: outOfPosition } = (td as HTMLElement).getBoundingClientRect();

            const headerHeight = this.TABLE_HEADER_HEIGHT + this.TOOLBAR_HEIGHT;
            if (outOfPosition - headerHeight <= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    top: -height,
                });
            }

            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_UP;
        }
    };

    hasUnsavedChanges() {
        if (!this.worksheets || !this.worksheets.getData() || !this.userCanEdit) return false;
        const currentData = this.worksheets.getData();

        if (currentData && this.isThermalK() && currentData[16]) currentData[16][1] = '';

        if (!_.isEqual(currentData, this.initalSensorRows)) {
            return true;
        }

        const currentStyles = this.worksheets.getStyle();
        return this.excelStylesService.areStylesUpdated(currentStyles);
    }

    async save() {
        if (!this.hasUnsavedChanges() || this.tableHasErrors()) return;
        try {
            this.isLoading = true;
            this.cdr.detectChanges();
            const currentRowsData = this.worksheets.getData();
            if (currentRowsData && this.isThermalK() && currentRowsData[16]) {
                currentRowsData[16][1] = '';
            }
            if (!_.isEqual(currentRowsData, this.initalSensorRows)) {
                const updatedSensorData = getSensorData(currentRowsData, this.selectedSlot, this.outputType);
                const updatedChannels = await this.sensorCableTagService.update(updatedSensorData);
                this.sensorRows = prepareExcelData(
                    updatedChannels,
                    this.selectedSlot.ioType,
                    this.outputType,
                    this.outputStartPin,
                    this.dutyEngineers,
                    this.digitalOutputSettings,
                    this.analogOutputSettings,
                    this.consoleSettings
                );
                this.worksheets.setData(this.sensorRows);
                if (this.isThermalK()) {
                    const commsString = this.translateService.instant('sensor-cable-tag.thermalK.com');
                    this.worksheets.setValue('B17', commsString, true);
                }

                this.excelStylesService.setDefaultStyle(this.worksheets, this.selectedSlot.ioType, this.outputType);
                this.initalSensorRows = _.cloneDeep(this.sensorRows);
            }
            const currentStyles = this.worksheets.getStyle();
            if (this.excelStylesService.areStylesUpdated(currentStyles)) {
                await this.excelStylesService.saveStyleDifferences(currentStyles);
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    checkCellValidation(cell, colIndex, rowIndex, newValue) {
        if (cell.classList.contains('readonly')) return;

        this.worksheets.ignoreHistory = true;
        const error = getCellHasError(this.selectedSlot.ioType, colIndex, newValue, this.outputType);
        const columnLetter = String.fromCharCode(65 + colIndex);
        const cellKey = columnLetter + rowIndex;

        if (error) {
            this.cellHasErrors[cellKey] = error;
            cell.classList.add('error-border');
        } else if (this.cellHasErrors[cellKey]) {
            delete this.cellHasErrors[cellKey];
            cell.classList.remove('error-border');
        }
        this.worksheets.ignoreHistory = false;
    }

    isDISlot() {
        return isDISlot(this.selectedSlot.ioType);
    }

    isDOSlot() {
        return isDOSlot(this.selectedSlot.ioType);
    }

    isAISlot() {
        return isAISlot(this.selectedSlot.ioType);
    }

    isOutputSlot() {
        return isOutputSlot(this.selectedSlot.ioType);
    }

    isDONormalSlot() {
        return this.isDOSlot() && this.outputType == terminalType.normal;
    }

    isDORelaySlot() {
        return this.isDOSlot() && this.outputType == terminalType.relay;
    }

    isThermalK() {
        return isThermalK(this.selectedSlot.ioType);
    }

    tableHasErrors(): boolean {
        return Object.values(this.cellHasErrors).length > 0;
    }

    getTableErrors(): CellError[] {
        return Object.values(this.cellHasErrors);
    }

    async printSensorCableTable() {
        try {
            if (this.tableHasErrors()) return;
            if (this.hasUnsavedChanges()) {
                const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                    width: '474px',
                    data: {
                        type: ConfirmationDialogType.COMPLEX,
                        title: 'confirmation-dialog.print.title',
                        message: 'confirmation-dialog.print.message',
                        backButton: 'confirmation-dialog.print.back',
                        discardButton: 'confirmation-dialog.print.discard',
                        saveButton: 'confirmation-dialog.print.save',
                    },
                });
                const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

                if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                    return;
                } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                    await this.save();
                }
            }

            this.isLoading = true;

            const pdfFile = await this.sensorTagPrint.getSlotPrintFile(
                this.vesselId,
                this.selectedSlot.slotId,
                this.selectedSlot.ioType,
                this.selectedTerminal
            );
            const blob = new Blob([pdfFile], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);

            const printWindow = window.open(blobUrl, '_blank');
            if (printWindow) {
                printWindow.onload = function () {
                    printWindow.print();
                };
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    selectedFileChange(e: any) {
        this.importExcelFile(e.target.files[0]);
    }

    importExcelFile(file: any) {}

    get isTableEmpty() {
        if (!this.worksheets || this.sensorRows.length == 0) return true;
        return false;
    }

    // isImportButtonDisabled() {
    //     return false;
    // }

    // importButtonClick() {}

    // downloadButtonClick() {}
}
